/*    
<summary>
  This functional component "LoginForm" provides form to login into application on the basis of the credentials provided.
  Developer:Aashish Singh, Created Date:28-Aug-2022, Last Updated By: Mohammad Saquib Khan, Updated Date: 10-Feb-2023
</summary>
<returns>Returns JSX</returns>
*/

import React, { useEffect, useState } from "react";
import { Field, Form, Formik } from "formik";
import monozlogo from "../../../assets/Images/logo--white.png";
import monozlogoMobile from "../../../assets/Images/monoZ_Black.png";
import loginBackground from "../../../assets/Images/backgroundLoginSvg.svg"
import { FormattedMessage } from "react-intl";
import ILogin, { IUserTypeLogin } from "../../../models/forms/ILogin";
import hucksterLogo from "../../../assets/Images/logo.svg";
import FormikFormInput from "../../../shared-components/FormikFormHandlers/FormikFormInput";
import FormikFormCheckBox from "../../../shared-components/FormikFormHandlers/FormikFormCheckBox";
import { LoginValidateSchema, LoginValidateSchemaForStore } from "../../../shared-components/FormikFormHandlers/FormikValidationSchema";
import LoaderButton from "../../../shared-components/button/loader-button";
import Label from "../../../shared-components/label/label";
import { useStore } from "../../../contexts/store-provider";
import RoutesConstants from "../../../constants/routes.constant";
import { useHistory, useLocation } from "react-router-dom";
import { formatMessage } from "../../../translations/format-message";
import LanguageSlider from "../../../shared-components/language-slider/language-slider";

interface IProps {
  loginHandler: (data: IUserTypeLogin) => void;
  initialValues: IUserTypeLogin;
  isLoading: boolean;
}

let LoginForm = (props: IProps) => {
  const history = useHistory();
  const location = useLocation();
  const { preferencesStore } = useStore();
  const [showHidePassword, setShowHidePassword] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<{
    language: string;
    isLanguageChanged: boolean;
  }>({ language: preferencesStore.language, isLanguageChanged: false });

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "selectedLanguage.isLanguageChanged" of the useEffect changed.
   */
  useEffect(() => {
    if (selectedLanguage.isLanguageChanged) {
      preferencesStore.changeLanguage(selectedLanguage.language);
      setSelectedLanguage({ ...selectedLanguage, isLanguageChanged: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage.isLanguageChanged]);

  const forgotPassword = () => {
    history.push(RoutesConstants.ForgotPassword);
  }

  return (
    <div className={`bg-img bg-img${props.initialValues.userType} background`}>
      <Formik
        initialValues={props.initialValues}
        validationSchema={
          props.initialValues.userType !== 3
            ? LoginValidateSchema
            : LoginValidateSchemaForStore
        }
        validateOnBlur={false}
        onSubmit={async (values) => {
          props.loginHandler(values);
        }}
      >
        <Form className="login">
          <div className="display">
            <div className="d-flex justify-content-between align-items-center ">
              <div className="logoregister">
                <figure>
                  <img src={monozlogo} alt="logo" className="img-fluid"></img>
                </figure>
              </div>
              <LanguageSlider />
            </div>
            <h1 className="fleet_management_heading">
              <FormattedMessage id={(
                {
                  0: "SuperAdmin",
                  1: "Customer",
                  2: "Staff",
                  3: "Store",
                  4: "Group",
                  5: "HeadQuarter",
                }[props.initialValues.userType] || ""
              )} />
              &nbsp;
              <FormattedMessage id="login" />
            </h1>
          </div>
          <div className="loginBox">
            {props.initialValues.userType !== 3 ? (
              <div className="form-group email_wrapper">
                <Label
                  required={true}
                  label="user_label_login_id"
                  htmlFor="email"
                />
                <Field
                  data-testid="testUsername"
                  className="form-control"
                  name="email"
                  type="text"
                  invalidcharacter={[" "]}
                  placeholder="user_label_login_id"
                  validcharacter={[
                    "-",
                    "_",
                    "[",
                    "]",
                    "(",
                    ")",
                    ":",
                    "#",
                    "@",
                    ".",
                  ]}
                  as={FormikFormInput}
                />
              </div>
            ) : (
              <div className="form-group store_wrapper">
                <Label
                  className="label-style"
                  required={true}
                  label="store_code"
                  htmlFor="storeCode"
                />
                <Field
                  className="form-control"
                  name="storeCode"
                  type="text"
                  placeholder="enter_store_code"
                  as={FormikFormInput}
                  invalidcharacter={[
                    " ",
                    "!",
                    "@",
                    "#",
                    "$",
                    "%",
                    "^",
                    "&",
                    "*",
                    "(",
                    ")",
                    "-",
                    "_",
                    "=",
                    "+",
                    "[",
                    "]",
                    "{",
                    "}",
                    "|",
                    ";",
                    ":",
                    "<",
                    ">",
                    ",",
                    ".",
                    "/",
                    "?",
                    "~",
                    "`",
                    "'",
                    "\\",
                    '"',
                  ]}
                />
              </div>
            )}
            <div className="showHideOuterDiv form-group password_wrapper">
              <Label
                required={true}
                label="user_label_password"
                htmlFor="password"
              />
              <Field
                data-testid="testPassword"
                className="form-control"
                name="password"
                type={showHidePassword ? "text" : "password"}
                invalidcharacter={[" "]}
                validcharacter={[
                  "~",
                  "`",
                  "!",
                  "@",
                  "#",
                  "$",
                  "%",
                  "^",
                  "&",
                  "*",
                  "(",
                  ")",
                  "_",
                  "-",
                  "+",
                  "=",
                  "{",
                  "[",
                  "}",
                  "]",
                  "|",
                  "\\",
                  ":",
                  ";",
                  "'",
                  '"',
                  "<",
                  ",",
                  ">",
                  ".",
                  "?",
                  "/",
                ]}
                placeholder="user_label_password"
                as={FormikFormInput}
              />

              {showHidePassword ? (
                <svg
                  type="button"
                  onClick={() => {
                    setShowHidePassword(!showHidePassword);
                  }}
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  stroke="#000"
                  strokeWidth="2"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="showHidePass css-i6dzq1"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              ) : (
                <svg
                  type="button"
                  onClick={() => {
                    setShowHidePassword(!showHidePassword);
                  }}
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  stroke="#000"
                  strokeWidth="2"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="showHidePass css-i6dzq1"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              )}
            </div>
            <div className="">
              <label className="">
                <Field
                  data-testid="testRememberMe"
                  className="me-2"
                  name="rememberMe"
                  type="checkbox"
                  id="inlineFormCheck"
                  as={FormikFormCheckBox}
                />
                <FormattedMessage id="remember_me" />
              </label>
            </div>
          </div>
          <LoaderButton
            type="submit"
            className="btn btn-blue btn btn-primary"
            isLoading={props.isLoading}
            text="login"
          />
          <div className="footer-login">
            
            {/* <p className="login-as-customer" >
            {formatMessage("login_as_customer")} <a href="javascript:;" onClick={() => history.push(RoutesConstants.CustomerLogin)}>{formatMessage("login_here")}</a>
            </p> */}
          </div>
        </Form>
      </Formik>
    </div>
  );
};

export default LoginForm;
